/**
 * DocumentChangeCard - Rich card display for a single document (blob) change.
 *
 * Blob layout using shared change utilities:
 * - Header with file name and extension badge
 * - Key fields (collection_name, file_extension, file_mime_type, created_at, modified_at)
 * - Reference fields (file_hash, download_url, collection_id) in collapsible section
 * - Amber change indicators with old-value tooltips
 * - Transfer checkboxes + "Mark as Transferred" button
 */

import { FileText } from 'lucide-react';
import * as React from 'react';

import { Badge } from '@/components/ui/catalyst/badge';
import type { ChangedDocumentEntity } from '@/services/api/generated/crossMatterChangesService';

import {
  buildCheckableItems,
  type CheckableFieldConfig,
} from '../utils/buildCheckableItems';
import { useEntityChanges } from '../utils/useEntityChanges';
import { useTransferAction } from '../utils/useTransferAction';
import { useTransferChecklist } from '../utils/useTransferChecklist';
import { CollapsibleSection } from './CollapsibleSection';
import { EntityChangeCardShell } from './EntityChangeCardShell';
import { EntityField } from './EntityField';
import { EventsFallbackWithChecklist } from './EventsFallbackWithChecklist';
import { type FieldConfig, KeyFieldsGrid, KeyFieldsGridUnchanged } from './KeyFieldsGrid';
import { ShowMoreSection } from './ShowMoreSection';
import { TransferButton } from './TransferButton';
import { TransferCheckbox } from './TransferCheckbox';

// ============================================================================
// Blob config
// ============================================================================

const KEY_FIELDS: FieldConfig[] = [
  { key: 'collection_name', label: 'Collection' },
  { key: 'file_extension', label: 'Extension' },
  { key: 'file_mime_type', label: 'MIME Type' },
  { key: 'created_at', label: 'Created At', isDate: true },
  { key: 'modified_at', label: 'Modified At', isDate: true },
];

const REFERENCE_FIELDS: { key: string; label: string }[] = [
  { key: 'file_hash', label: 'File Hash' },
  { key: 'download_url', label: 'Download URL' },
  { key: 'collection_id', label: 'Collection ID' },
  { key: 'original_filename', label: 'Original Filename' },
];

const DOCUMENT_CHECKABLE_FIELDS: CheckableFieldConfig[] = [
  { key: 'collection_name', type: 'field' },
  { key: 'file_extension', type: 'field' },
  { key: 'file_mime_type', type: 'field' },
  { key: 'created_at', type: 'field' },
  { key: 'modified_at', type: 'field' },
  { key: 'file_hash', type: 'field' },
  { key: 'download_url', type: 'field' },
  { key: 'collection_id', type: 'field' },
  { key: 'original_filename', type: 'field' },
];

// ============================================================================
// Main Component
// ============================================================================

export const DocumentChangeCard: React.FC<{
  entity: ChangedDocumentEntity;
}> = ({ entity }) => {
  const displayData =
    entity.change_type === 'DELETED'
      ? (entity.old ?? entity.current)
      : (entity.current ?? entity.old);

  const changes = useEntityChanges(
    entity.change_type,
    entity.old,
    entity.current,
  );

  const checkableItems = React.useMemo<string[]>(
    () =>
      buildCheckableItems(
        entity.change_type,
        displayData as Record<string, unknown> | undefined,
        entity.events,
        changes,
        DOCUMENT_CHECKABLE_FIELDS,
      ),
    [entity.change_type, entity.events, changes, displayData],
  );

  const checklist = useTransferChecklist(entity.change_type, checkableItems);
  const transferEventIds = React.useMemo(() => entity.events.map((e) => e.id), [entity.events]);
  const transfer = useTransferAction(transferEventIds);

  if (!displayData) return null;

  const data = displayData as unknown as Record<string, unknown>;

  // Extension for badge
  const fileExtension = displayData.file_extension;

  // Reference fields visibility
  const visibleReferenceFields = REFERENCE_FIELDS.filter(
    (f) => data[f.key] || changes.isFieldChanged(f.key),
  );
  const changedReferenceFields = visibleReferenceFields.filter((f) =>
    changes.isFieldChanged(f.key),
  );
  const unchangedReferenceFields = visibleReferenceFields.filter(
    (f) => !changes.isFieldChanged(f.key),
  );

  // Events fallback
  const isUpdatedWithoutDiffs =
    entity.change_type === 'UPDATED' && changes.totalChanges === 0;

  const hasExpandedContent =
    KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue || changes.isFieldChanged(f.key);
    }) ||
    visibleReferenceFields.length > 0 ||
    isUpdatedWithoutDiffs;

  // Unchanged content for ShowMoreSection
  const hasUnchangedContent =
    (entity.change_type === 'UPDATED' || entity.change_type === 'SAME') &&
    (KEY_FIELDS.some((f) => {
      const val = data[f.key];
      const hasValue = val !== null && val !== undefined && val !== '';
      return hasValue && !changes.isFieldChanged(f.key);
    }) ||
      unchangedReferenceFields.length > 0);

  return (
    <EntityChangeCardShell
      changeType={entity.change_type}
      name={entity.name}
      description={entity.description}
      icon={FileText}
      iconBgClassName="bg-sky-50"
      iconTextClassName="text-sky-600"
      badges={
        fileExtension ? (
          <Badge color="zinc" className="text-[10px] shrink-0">
            {fileExtension}
          </Badge>
        ) : undefined
      }
      changeCount={changes.totalChanges}
      eventCount={entity.events.length}
      checklist={checklist}
      hasExpandedContent={hasExpandedContent}
    >
      {/* Events fallback */}
      {isUpdatedWithoutDiffs && (
        <EventsFallbackWithChecklist
          events={entity.events}
          checklist={checklist}
        />
      )}

      {/* Key fields grid */}
      <KeyFieldsGrid
        fields={KEY_FIELDS}
        data={data}
        changes={changes}
        checklist={checklist}
        changeType={entity.change_type}
      />

      {/* Reference fields (changed only) */}
      {changedReferenceFields.length > 0 && (
        <CollapsibleSection
          title="References"
          count={changedReferenceFields.length}
          changeType={entity.change_type}
          hasChanges
        >
          {changedReferenceFields.map((f) => (
            <div key={f.key} className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <EntityField
                  label={f.label}
                  value={data[f.key]}
                  fieldKey={f.key}
                  changes={changes}
                />
              </div>
              {checklist.showCheckboxes && changes.isFieldChanged(f.key) && (
                <TransferCheckbox
                  checked={checklist.isChecked(f.key)}
                  onToggle={() => checklist.toggle(f.key)}
                  className="mt-2.5"
                />
              )}
            </div>
          ))}
        </CollapsibleSection>
      )}

      {/* Show More Section for unchanged content */}
      {hasUnchangedContent && (
        <ShowMoreSection changeType={entity.change_type}>
          {/* Unchanged key fields */}
          <KeyFieldsGridUnchanged
            fields={KEY_FIELDS}
            data={data}
            changes={changes}
            checklist={checklist}
          />

          {/* Unchanged reference fields */}
          {unchangedReferenceFields.length > 0 && (
            <CollapsibleSection
              title="References"
              count={unchangedReferenceFields.length}
              changeType={entity.change_type}
              hasChanges={false}
            >
              {unchangedReferenceFields.map((f) => (
                <div key={f.key} className="flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <EntityField
                      label={f.label}
                      value={data[f.key]}
                      fieldKey={f.key}
                      changes={changes}
                    />
                  </div>
                </div>
              ))}
            </CollapsibleSection>
          )}
        </ShowMoreSection>
      )}

      {/* Transfer button */}
      {checklist.showButton && (
        <TransferButton
          enabled={checklist.buttonEnabled}
          onClick={transfer.handleTransfer}
          onSkip={transfer.handleSkip}
          isLoading={transfer.isLoading}
          status={transfer.status}
        />
      )}
    </EntityChangeCardShell>
  );
};
